import { ServiceBase } from "@app/shared";
import { AccessCriteriaResult, ServicingCriteriaResult } from "./results";
import TemplateCollectionMetadata, { ServicingAnalysisGranularityEnum, TemplateCategory, TemplateMetadata, TemplateSpecialtyGroup, CCIIOTemplateMetaData, CalculationMethodEnum } from "./templateModels";
import { ResourceType } from "@app/resources";

/** Manages interaction with the template set API. */
export default class TemplateService extends ServiceBase {
  /**
   * Returns metadata about all template collections and associated templates for the current user.
   * Does not include the actual template definition, just information that might be useful for the UI - name, description, etc.
   */
  public async getTemplateCollections(collectionType?: ResourceType): Promise<TemplateCollectionMetadata[]> {
    return await this.makeGet<TemplateCollectionMetadata[]>("templates", { collectionType });
  }

   /**
   * Returns metadata about all unarchived template collections and associated unarchived templates for the current user.
   * Does not include the actual template definition, just information that might be useful for the UI - name, description, etc.
   */
   public async getUnarchivedTemplateCollections(collectionType?: ResourceType): Promise<TemplateCollectionMetadata[]> {
    return await this.makeGet<TemplateCollectionMetadata[]>("templates/unarchivedTemplateCollectionsWithUnarchivedTemplates", { collectionType });
  }

  /**
   * Returns metadata about a template collection and associated templates. Does not include the actual template definition, just information that might be useful for the UI - name, description, etc.
   * @param id The template collection's identifier.
   */
  public async getTemplateCollectionById(id: number): Promise<TemplateCollectionMetadata> {
    return await this.makeGet<TemplateCollectionMetadata>(`templates/${id}`);
  }

  /**
   * Returns metadata about a specific template in a template collection. Does not include the actual template definition, just information that might be useful for the UI - name, description, etc.
   * @param id The template collection's identifier.
   * @param templateId The template's identifier.
   * @param getServicingGranularity Indicates whether to retrieve servicing granularity form the API.
   */
  public async getTemplateById(id: number, templateId: string, getServicingGranularity: boolean): Promise<TemplateMetadata> {
    const metadata = await this.makeGet<TemplateMetadata>(`templates/${id}/template/${templateId}`);
    if (getServicingGranularity) {
      metadata.servicingAnalysisGranularity =
        await this.makeGet<ServicingAnalysisGranularityEnum>(
          `templates/${id}/template/${templateId}/servicingAnalysisGranularity`);
    }
    return metadata;
  }

  /**
   * return metadata about the specific CCIIO provider template*/
  public async GetCCIIOProviderSetTemplateInfo(): Promise<CCIIOTemplateMetaData> {
    return await this.makeGet<CCIIOTemplateMetaData>(`templates/cciioTemplate`);
  }

  /**
   * Returns metadata about the latest template in a template collection for a given correlation ID. Does not include the actual template definition, just information that might be useful for the UI - name, description, etc.
   * @param id The template collection's identifier.
   * @param templateCorrelationId The template correlation ID.
   * @param getServicingGranularity Indicates whether to retrieve servicing granularity form the API.
   */
  public async getLatestTemplateByCorrelationId(id: number, templateCorrelationId: string, getServicingGranularity: boolean): Promise<TemplateMetadata> {
    const metadata = await this.makeGet<TemplateMetadata>(`templates/${id}/correlationIds/${encodeURIComponent(templateCorrelationId)}/latestTemplate`);
    if (metadata && getServicingGranularity) {
      metadata.servicingAnalysisGranularity =
        await this.makeGet<ServicingAnalysisGranularityEnum>(
          `templates/${id}/template/${metadata.templateId}/servicingAnalysisGranularity`);
    }
    return metadata;
  }

  /**
   * Returns detailed information about a specific template in a template collection. Will be a huge response for most templates so use with caution.
   * @param id The template collection's identifier.
   * @param templateId The template's identifier.
   */
  public async getTemplateDetailsById(id: number, templateId: string): Promise<TemplateMetadata> {
    //TODO: STUB - This endpoint is not yet configured.
    //Once it is, this method will not be returning just metadata, and the
    //extra call to get the servicingAnalysisGranularity should be unnecessary,
    //since it will be included in the template data.
    const metadata = await this.makeGet<TemplateMetadata>(`templates/${id}/template/${templateId}/details`);
    metadata.servicingAnalysisGranularity =
      await this.makeGet<ServicingAnalysisGranularityEnum>(
        `templates/${id}/template/${templateId}/servicingAnalysisGranularity`);
    return metadata;
  }

  /**
   * Returns the specialty groups associated with a particular template.
   * @param id The template collection's identifier.
   * @param templateId The template's identifier.
   */
  public async getTemplateSpecialtyGroups(id: number, templateId: string): Promise<TemplateSpecialtyGroup[]> {
    return await this.makeGet<TemplateSpecialtyGroup[]>(`templates/${id}/template/${templateId}/specialtygroups`);
  }

  /**
 * Returns the specialty group categories associated with a particular template.
 * @param id The template collection's identifier.
 * @param templateId The template's identifier.
 */
  public async getTemplateCategories(id: number, templateId: string): Promise<TemplateCategory[]> {
    return await this.makeGet<TemplateCategory[]>(`templates/${id}/template/${templateId}/categories`);
  }

  /**
   * Returns the specialty groups associated with a particular template.
   * @param id The template collection's identifier.
   * @param templateCorrelationId The template's correlation identifier.
   */
  public async getLatestTemplateSpecialtyGroupsByCorrelationId(id: number, templateCorrelationId: string): Promise<TemplateSpecialtyGroup[]> {
    return await this.makeGet<TemplateSpecialtyGroup[]>(`templates/${id}/template/${encodeURIComponent(templateCorrelationId)}/latestTemplateSpecialtygroups`);
  }

  /**
 * Returns the specialty group categories associated with a particular template.
 * @param id The template collection's identifier.
 * @param templateCorrelationId The template's correlation identifier.
 */
  public async getLatestTemplateCategoriesByCorrelationId(id: number, templateCorrelationId: string): Promise<TemplateCategory[]> {
    return await this.makeGet<TemplateCategory[]>(`templates/${id}/template/${encodeURIComponent(templateCorrelationId)}/latestTemplateCategories`);
  }

  /**
   * Returns the access criteria for a particular specialty group.
   * @param id The template collection's identifier.
   * @param templateId The template's identifier.
   * @param specialtyGroupCode The specialty group code.
   * @param isTelehealthSpecialtyGroup Should be true, if this is a telehealth specialty group in the project.
   */
  public async getTemplateSpecialtyGroupAccessCriteria(id: number, templateId: string, specialtyGroupCode: string, isTelehealthSpecialtyGroup: boolean, skip?: number, take?: number): Promise<AccessCriteriaResult> {
    return await this.makeGet<AccessCriteriaResult>(`templates/${id}/template/${templateId}/accessCriteria`,
      { isTelehealthSpecialtyGroup, skip, specialtyGroupCode, take });
  }

  /**
   * Returns the servicing criteria for a particular specialty group.
   * @param id The template collection's identifier.
   * @param templateId The template's identifier.
   * @param specialtyGroupCode The specialty group code.
   */
  public async getTemplateSpecialtyGroupServicingCriteria(id: number, templateId: string, specialtyGroupCode: string, skip?: number, take?: number): Promise<ServicingCriteriaResult> {
    return await this.makeGet<ServicingCriteriaResult>(`templates/${id}/template/${templateId}/servicingCriteria`,
      { skip, specialtyGroupCode, take });
  }

  public async getPriorityCmsTemplates(): Promise<string[]> {
    return await this.makeGet<string[]>("templates/prioritycms");
  }

  public async getCalculationMethod(runConfigurationId: number, specialtyGroupCode: string): Promise<CalculationMethodEnum> {
    return await this.makeGet(`templates/get-calculation-method/run-configuration/${runConfigurationId}/specialty-group-code/${specialtyGroupCode}`);
  }
}
